/**
 * Junta tudo o que a Capacidade da Fábrica precisa e calcula o painel com o
 * domínio puro (`utils/capacidade`):
 *
 * - a planilha já lida pelo DadosContext (`extratoCapacidade`, lido por
 *   cabeçalho no mesmo worker do parse);
 * - o calendário da unidade (dias sem expediente);
 * - o histórico guardado no backend: términos de anos anteriores e as fotos
 *   (para a aba Precisão);
 * - as exceções de carga marcadas pelo PCP (leve, normal, pesado), que
 *   alimentam a leitura com pesos. Elas não seguram a página: sem elas a
 *   conta é a contagem, que é a régua oficial até as marcas cobrirem o
 *   histórico;
 * - as vendas assinadas aguardando o PCP, que entram na fila como camada
 *   própria até serem ligadas a um pedido da planilha. Também não seguram a
 *   página: sem elas a fila é só a da planilha.
 *
 * Também ENVIA a foto da versão atual da planilha quando ela veio do Drive:
 * é assim que o histórico cresce sem ninguém lembrar de guardar cópia. Só a
 * cópia do Drive gera foto (todos veem a mesma coisa); o backend é
 * idempotente pela versão, então reenviar não duplica nada.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../../context/AuthContext";
import { FONTE_SERVIDOR, useDados } from "../../context/DadosContext";
import useCalendarioUnidade from "../../hooks/useCalendarioUnidade";
import useExcecoesCapacidade from "../../hooks/useExcecoesCapacidade";
import useVendasAssinadas from "../../hooks/useVendasAssinadas";
import { chaveAmbiente } from "../MapaProducao/dados/chaves";
import { enviarFoto, listarFotos, listarTerminos } from "../../services/planilhaLegado";
import {
  ESCALA,
  QUANTIDADE_PADRAO,
  calcularPainelCapacidade,
  hojeIso,
  inicioDoMes,
  janelaDoRitmo,
  linhasParaFoto,
  somarMeses,
} from "../../utils/capacidade";
import { importarCopiaAntiga } from "./importacao";

const ORIGEM_DRIVE = "drive";
const HTTP_CONFLITO = 409;

// Versões já enviadas nesta sessão (por usuário + versão do arquivo + versão
// do leitor). Fica no módulo para sobreviver a sair e voltar da página.
const versoesEnviadas = new Set();

/** Só para testes. */
export function esquecerFotosEnviadas() {
  versoesEnviadas.clear();
}

/**
 * Primeiro dia do histórico pedido ao backend: a janela do ritmo (28
 * semanas) antes do período passado mais antigo exibido (6 meses) — e antes
 * da foto mais antiga com projeção, para a aba Precisão contar os términos
 * que vieram depois dela.
 */
export function inicioDoHistorico(hoje, fotos = []) {
  const periodoMaisAntigo = inicioDoMes(somarMeses(hoje, -QUANTIDADE_PADRAO[ESCALA.MES].anteriores));
  const base = janelaDoRitmo(periodoMaisAntigo).de;
  return (fotos || [])
    .map((foto) => foto?.projecao?.hoje)
    .filter(Boolean)
    .reduce((menor, data) => (data < menor ? data : menor), base);
}

const HISTORICO_VAZIO = Object.freeze({ fotos: [], terminos: [], carregado: false, erro: null });

function useHistorico(request, hoje) {
  const [historico, setHistorico] = useState(HISTORICO_VAZIO);
  const [rodada, setRodada] = useState(0);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const respostaFotos = await listarFotos(request);
        const fotos = Array.isArray(respostaFotos?.fotos) ? respostaFotos.fotos : [];
        const respostaTerminos = await listarTerminos(request, { desde: inicioDoHistorico(hoje, fotos) });
        const terminos = Array.isArray(respostaTerminos?.terminos) ? respostaTerminos.terminos : [];
        if (vivo) setHistorico({ fotos, terminos, carregado: true, erro: null });
      } catch (erro) {
        if (vivo) {
          setHistorico({
            ...HISTORICO_VAZIO,
            carregado: true,
            erro: erro?.message || "Não foi possível ler o histórico da planilha.",
          });
        }
      }
    })();
    return () => {
      vivo = false;
    };
  }, [request, hoje, rodada]);

  const recarregar = useCallback(() => setRodada((n) => n + 1), []);
  return { ...historico, recarregar };
}

function linhasPorChave(dados) {
  const mapa = new Map();
  (dados || []).forEach((row) => {
    const chave = chaveAmbiente(row);
    // Chave repetida: vale a última linha, a mesma regra do extrato.
    if (chave) mapa.set(chave, row);
  });
  return mapa;
}

/**
 * Envia a foto da versão do Drive uma vez por sessão. 409 (a versão já não é
 * a atual do Drive) é esperado e ignorado; outros erros não atrapalham a
 * página (o próximo acesso tenta de novo) e só aparecem no console de dev.
 */
function useEnvioDaFoto({ request, usuarioId, fonte, versaoArquivo, extrato, painel, nomeArquivo, hoje, aoCriar }) {
  useEffect(() => {
    if (fonte !== FONTE_SERVIDOR || !versaoArquivo || !extrato?.ok || !painel?.ok) return;
    const chave = `${usuarioId ?? ""}:${versaoArquivo}:${extrato.versaoLeitor}`;
    if (versoesEnviadas.has(chave)) return;
    versoesEnviadas.add(chave);
    enviarFoto(request, {
      origem: ORIGEM_DRIVE,
      versao_arquivo: versaoArquivo,
      versao_leitor: extrato.versaoLeitor,
      // O servidor decide a data das fotos do Drive; vai só por contrato.
      data_referencia: hoje,
      nome_arquivo: nomeArquivo || "",
      linhas: linhasParaFoto(extrato.linhas),
      projecao: painel.projecaoFoto || null,
    })
      .then((resposta) => {
        if (resposta?.criada) aoCriar();
      })
      .catch((erro) => {
        if (erro?.status === HTTP_CONFLITO) return;
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.warn("Capacidade da Fábrica: a foto da planilha não foi guardada.", erro);
        }
      });
  }, [request, usuarioId, fonte, versaoArquivo, extrato, painel, nomeArquivo, hoje, aoCriar]);
}

/**
 * `{estado, erroPlanilha, painel, hoje, calendario, historico, excecoes,
 * vendas, linhaDaPlanilha, planilha, importarCopia, recarregar}`.
 * `excecoes` = `useExcecoesCapacidade()` (marcas, `podeMarcar`, `marcar`);
 * `vendas` = `useVendasAssinadas()` (aguardando, `registrar`, `ligar`, `cancelar`).
 *
 * `estado`: "carregando" | "erro_planilha" | "sem_planilha" | "layout" |
 * "erro_calendario" | "erro_historico" | "pronto".
 */
export default function useCapacidadeFabrica() {
  const {
    dados,
    meta,
    erro: erroPlanilha,
    initialLoading,
    extratoCapacidade,
    versaoArquivo,
    fonte,
    atualizadoEm,
  } = useDados();
  const { request, usuario } = useAuth();
  const calendario = useCalendarioUnidade();
  // "Hoje" fica fixo enquanto a página está aberta: virar o dia no meio da
  // leitura mudaria todos os números sem ninguém pedir.
  const [hoje] = useState(() => hojeIso());
  const historico = useHistorico(request, hoje);
  const excecoes = useExcecoesCapacidade();
  const { excecoes: marcas } = excecoes;
  const vendas = useVendasAssinadas();
  const { vendas: vendasAssinadas } = vendas;
  const { naoUteis, anosComRegistro, recarregar: recarregarCalendario } = calendario;
  const { recarregar: recarregarHistorico } = historico;

  const prontoParaCalcular =
    Boolean(extratoCapacidade?.ok) &&
    calendario.carregado &&
    !calendario.erro &&
    historico.carregado &&
    !historico.erro;

  const { terminos: terminosBackend, fotos } = historico;
  const painel = useMemo(() => {
    if (extratoCapacidade && !extratoCapacidade.ok) {
      return calcularPainelCapacidade({ extrato: extratoCapacidade, hoje, naoUteis });
    }
    if (!prontoParaCalcular) return null;
    return calcularPainelCapacidade({
      extrato: extratoCapacidade,
      terminosBackend,
      fotos,
      naoUteis,
      anosComRegistro,
      hoje,
      marcas,
      vendasAssinadas,
    });
  }, [
    extratoCapacidade,
    prontoParaCalcular,
    terminosBackend,
    fotos,
    naoUteis,
    anosComRegistro,
    hoje,
    marcas,
    vendasAssinadas,
  ]);

  useEnvioDaFoto({
    request,
    usuarioId: usuario?.id,
    fonte,
    versaoArquivo,
    extrato: extratoCapacidade,
    painel,
    nomeArquivo: meta?.arquivoLido,
    hoje,
    aoCriar: recarregarHistorico,
  });

  const linhaDaPlanilha = useMemo(() => linhasPorChave(dados), [dados]);

  const importarCopia = useCallback(
    async ({ arquivo, dataReferencia }) => {
      const resposta = await importarCopiaAntiga({ request, arquivo, dataReferencia, naoUteis, anosComRegistro });
      recarregarHistorico();
      return resposta;
    },
    [request, naoUteis, anosComRegistro, recarregarHistorico],
  );

  const recarregar = useCallback(() => {
    recarregarCalendario();
    recarregarHistorico();
  }, [recarregarCalendario, recarregarHistorico]);

  let estado = "pronto";
  if (initialLoading) estado = "carregando";
  else if (erroPlanilha) estado = "erro_planilha";
  else if (!extratoCapacidade) estado = "sem_planilha";
  else if (!extratoCapacidade.ok) estado = "layout";
  else if (calendario.carregado && calendario.erro) estado = "erro_calendario";
  else if (historico.carregado && historico.erro) estado = "erro_historico";
  else if (!painel) estado = "carregando";

  return {
    estado,
    erroPlanilha,
    painel,
    hoje,
    calendario,
    historico,
    excecoes,
    vendas,
    linhaDaPlanilha,
    planilha: { fonte, atualizadoEm, versaoArquivo, nomeArquivo: meta?.arquivoLido || null, temLinhas: dados.length > 0 },
    importarCopia,
    recarregar,
  };
}
